import { Modals, useModals } from "@/hooks/useModals"
import { Fragment } from "react"
import Button from "@/components/Button"
import Modal from "@/components/Modal"
import { useForm } from "@inertiajs/react"
import { Event } from "@/types/serializers"
import EventSelect from "@/components/EventSelect"
import ErrorMessage from "@/components/ErrorMessage"

const DeleteEventModal = ({ event }: { event: Event }) => {
  const { activeModal, closeModal } = useModals()
  const isOpen = activeModal === Modals.DeleteEvent

  const gamesCount = event.games?.length ?? 0
  const gamePlural = gamesCount > 1 ? "games" : "game"

  const deleteChoices = {
    keep: `Keep ${gamePlural} without event`,
    move: `Move ${gamePlural} to another event`,
    delete: `Delete ${gamePlural}`,
  } as const

  const {
    data,
    setData,
    setError,
    delete: destroy,
    processing,
    errors,
  } = useForm({
    action_on_games: "keep",
    target_event_id: "",
  })

  const deleteEvent = (e: React.FormEvent) => {
    e.preventDefault()

    if (data.action_on_games === "move" && !data.target_event_id) {
      setError("target_event_id", "Please select an event")
      return
    } else {
      setError("target_event_id", "")
    }

    destroy(`/events/${event.id}`, {
      async: true,
      onSuccess: () => closeModal(),
    })
  }

  return (
    <Modal show={isOpen} onClose={closeModal} title="Delete event">
      <div className="mt-4 space-y-4">
        <h3 className="text-gray-900">
          What do you want to do with the {gamesCount} {gamePlural} in this
          event?
        </h3>
        <form onSubmit={deleteEvent} className="space-y-8">
          <fieldset>
            <div className="space-y-4">
              {Object.keys(deleteChoices).map((key) => (
                <Fragment key={key}>
                  <div className="flex items-center">
                    <input
                      id={key}
                      value={key}
                      type="radio"
                      className="text-chessdrop-600 focus:ring-chessdrop-600 h-4 w-4 border-gray-300"
                      checked={data.action_on_games === key}
                      onChange={(e) =>
                        setData("action_on_games", e.target.value)
                      }
                    />
                    <label
                      htmlFor={key}
                      className="ml-3 block text-sm leading-6 font-medium text-gray-900"
                    >
                      {deleteChoices[key as keyof typeof deleteChoices]}
                    </label>
                  </div>
                  {key === "move" && data.action_on_games === "move" && (
                    <div className="ml-8 max-w-xs rounded-lg border border-gray-200 bg-gray-100 px-4 pt-2 pb-4 shadow-inner">
                      <EventSelect
                        value={data.target_event_id}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                          setData("target_event_id", e.target.value)
                        }
                        hideEventId={event.id}
                      />
                      {errors?.target_event_id ? (
                        <p className="mt-1 text-sm font-medium text-red-600">
                          {errors.target_event_id}
                        </p>
                      ) : null}
                    </div>
                  )}
                </Fragment>
              ))}
            </div>
          </fieldset>

          <div>
            <Button loading={processing} type="submit" fullWidth>
              Delete event
            </Button>

            <ErrorMessage
              className="mt-4 text-center"
              message={(errors as any).base}
            />
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default DeleteEventModal
